import { useCallback, useState } from "react";
import { trackEvent } from "@/tracking";
import { ChatWidgetConfiguration } from "@/types";
import { useTomisChatApi } from "./useTomisChatApi";
import { useIsMobile } from "./useIsMobile";

interface UseChatWidgetParams {
  /** Chat widget configuration stored in database */
  config: ChatWidgetConfiguration;
  /** If `true` load chat widget inside a <div> on the page */
  embed: boolean;
  /** Callback function to run after mount and the chat API is initialized */
  onReady: () => any;
}

/**
 * Comprehensive hook that manages all chat widget state and behavior
 * Handles widget open/close state, visibility, proactive messages, and API setup
 */
export const useChatWidget = ({
  config,
  embed,
  onReady,
}: UseChatWidgetParams) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHidden, setHidden] = useState(Boolean(config.hide));
  const [proactiveMessages, setProactiveMessages] = useState<string[]>([]);

  const isMobileDevice = useIsMobile();

  const openWidget = useCallback(() => {
    setIsOpen(true);
    trackEvent("window_opened", { embed });
  }, [embed]);

  const closeWidget = useCallback(() => {
    setIsOpen(false);
    trackEvent("window_closed", { embed });
  }, [embed]);

  const handleOpenAndLaunch = useCallback(() => {
    setProactiveMessages([]);
    openWidget();
  }, [openWidget]);

  // Setup the chat API
  useTomisChatApi({
    embed,
    openWidget,
    closeWidget,
    setHidden,
    setProactiveMessages,
    onReady,
  });

  // Calculate positioning styles
  const side = config.position;
  const launcherStyle = {
    bottom: isMobileDevice
      ? config.spacingMobile.bottom
      : config.spacing.bottom,
    [side]: isMobileDevice ? config.spacingMobile.side : config.spacing.side,
  };
  const chatWindowStyle = isMobileDevice
    ? undefined
    : {
        bottom: config.spacing.bottom,
        [side]: config.spacing.side,
      };

  return {
    // State
    isOpen,
    isHidden,
    proactiveMessages,

    // Actions
    openWidget,
    closeWidget,
    handleOpenAndLaunch,
    setProactiveMessages,

    // Styles
    launcherStyle,
    chatWindowStyle,
  };
};
